/**
 * 「配属が無い従業員」の判定（運用開始チェックリストの警告用）。
 *
 * 🔴 **なぜ要るか**（2026-10-18・本番で実測）。
 * `EmployeeController@store` は配属（`employee_assignments`）を作らない。配属は
 * `/admin/organization` の別ステップなので、**登録しただけでは店舗に紐づかない**。
 * ところが運用開始チェックリストの「従業員を登録」は**行が 1 件あれば完了**になる
 * （`hasEmployee` = CAST/STAFF が 1 名以上）ので、オーナーは全部緑にできる。
 *
 * 実際に本番で起きた形: 導入店（会社 11）のキャスト 7 人（2026-10-13 登録）に
 * 配属の行が**削除済み・無効を含めて 1 件も無かった**。結果:
 * - 🔴 **伝票・オーダーにそのキャストを付けられない**（`RoleGuardService::canBeCastOnVisit` →
 *   `canAccessStoreOn` が `store_id` 一致の配属を要求する）。`cast_id` が入らないので
 *   `BackTransactionSyncService` が `continue` で落とす → **バックが 0 円**。
 *   ⚠️ **「区画が確定しないから 0 円」ではない**。`BackPolicyEngine` は `employment_type` が null のとき
 *   Step0 を丸ごと飛ばす（fail-open）ので、区画が未確定でもポリシーは当たる。
 *   次に同じ症状を調べる人が `BackPolicyPartitionResolver` を疑わないように書いておく。
 * - `CastEngagementTimeline::employmentTypeAt()` が null → バックの区画が確定しない
 * - 保存前の試算はその人を母数から落とす（`BackPolicyController@preview`）ので
 *   オーナーには「影響なし」に見える
 * - 実測: 伝票 3 件・明細 8 件があるのに **バックは 0 件**
 *
 * 気づける場所がキャスト一覧の「バックルール」列だけだったので、チェックリストにも出す。
 *
 * 🔴 **`hasEmployee` の意味は変えない**。運用中の会社のチェックリストを巻き戻すと、
 * 完了済みのパネルが再表示される（UX 監査 A-3 の前科）。**警告を別に足す**だけにする。
 *
 * 🔴 判定をコンポーネントに直接書かない（`CLAUDE.md` §5。画面に書いた分岐は
 * テストが当たらず 2026-10-15 に同じ形で 4 回壊れた）。
 */

import { getLocalDate } from '../utils/date';

/** 判定に要る配属の形（モデルの一部だけを見る）。 */
export interface AssignmentLike {
  store_id?: number | string | null;
  start_date?: string | null;
  end_date?: string | null;
  is_active?: boolean | null;
}

/** 判定に要る従業員の形。 */
export interface EmployeeLike {
  id?: number | string | null;
  // 🔴 **配属にはアカウント（`user_id`）が必須**（FE の検証も BE の
  //    `'user_id' => 'required|exists:users,id'` も要求する）。
  //    `employees.user_id` は**招待を受諾して初めて埋まる**ので、
  //    登録しただけの人は**配属できない**。警告の案内を分けるために見る。
  user_id?: number | string | null;
  // 🔴 **`user_id` が無い理由は 2 つあり、やることが正反対**。
  //    `null` = **まだ招待を送っていない**（店側がやる）
  //    `'invited'` = 送ったが未受諾（本人がやる・期限切れなら再送）
  //    `'accepted'` = 受諾済み（＝`user_id` が埋まる）
  //    `EmployeeController@index` が LINE とメールの新しい方から導出して載せる
  //    （`invitations:id,employee_id,method,accepted_at` を eager load している）。
  latest_invitation_status?: string | null;
  role_type?: 'ADMIN' | 'STAFF' | 'CAST' | string | null;
  status?: 'active' | 'inactive' | 'suspended' | 'retired' | string | null;
  employee_assignments?: AssignmentLike[] | null;
}

/**
 * 日付を**ローカルの YYYY-MM-DD** に直す。
 *
 * 🔴 **`slice(0, 10)` で取らない**。`EmployeeAssignment` の `start_date` / `end_date` は
 * `'date'` キャストだけで `serializeDate` を持たないので、Laravel 既定の **UTC の ISO 文字列**で
 * 降ってくる（実測: DB の `2025-09-08` が `"2025-09-07T15:00:00.000000Z"`）。
 * 先頭 10 文字を切ると**常に前日**になり、最終日の人が「配属なし」に見える。
 */
export function assignmentYmd(v: unknown): string | null {
  if (v == null || v === '') return null;
  const d = new Date(v as string);
  if (Number.isNaN(d.getTime())) return null;
  return getLocalDate(d);
}

/**
 * その配属が `today` に効いているか。
 *
 * サーバ（`BackPolicyPartitionResolver::resolveCells`）は **`is_active` と期間**で絞るので揃える。
 * ⚠️ `is_active` が未定義のときは「効いている」とみなす（古い応答で警告が暴発しないように）。
 */
export function isAssignmentEffective(a: AssignmentLike | null | undefined, today: string): boolean {
  if (!a) return false;
  if (a.is_active === false) return false;
  // 🔴 **店舗配属だけを数える**。`assignment_type` は `['company', 'office', 'store']` で、
  // 事業所・会社配属は `store_id` が入らない。サーバはどの経路でも**店舗配属しか見ない**ので
  // （`RoleGuardService::canAccessStoreOn` が `store_id` 一致を要求する）、
  // 事業所配属の行だけを持つ人は打刻も伝票への割当もできず、**今回と同じ症状になる**。
  // ⚠️ 新規作成の画面では事業所を選べないが、既存の事業所配属を訂正する編集パスは残っている。
  if (a.store_id == null || a.store_id === "") return false;
  const from = assignmentYmd(a.start_date);
  const to = assignmentYmd(a.end_date);
  if (from !== null && from > today) return false;
  if (to !== null && to < today) return false;
  return true;
}

/**
 * 配属を持つべき従業員か。
 *
 * ⚠️ **ADMIN は配属不要**（`accessibleStoreIds` が ADMIN だけ null）。
 * ⚠️ **退職者は除く**（配属が無いのは当然なので、警告に出すと毎回赤くなる）。
 * ⚠️ `role_type` が無い行は数えない（判定材料が無いので黙る方を選ぶ）。
 */
export function needsAssignment(e: EmployeeLike | null | undefined): boolean {
  if (!e) return false;
  if (e.status === 'retired') return false;
  return e.role_type === 'CAST' || e.role_type === 'STAFF';
}

/**
 * **開始日が先**の店舗配属を持っているか。
 *
 * 🔴 これを「配属あり」に寄せる（警告に出さない）。体入予定者を前月に登録する店では
 * **正しく配属しているのに常時赤くなる**（2026-10-18・レビュー指摘）。
 * ⚠️ サーバは開始日前のキャストを伝票に付けないので「まだ使えない」のは事実だが、
 * オーナーに見せる言葉としては「配属していない」ではない。
 */
// 🔴 **`today` に既定値を置かない**（2026-10-18・レビュー指摘）。
// 既定値を通る assert は `hasFutureAssignment` の追加で構造的に空振りし、
// 既定を `new Date().toISOString().slice(0,10)` に書き換えても CI が緑のままだった。
// 呼び側が `getLocalDate()` を渡す形にして、未テストの経路を無くす。
export function hasFutureAssignment(
  e: EmployeeLike | null | undefined,
  today: string,
): boolean {
  const assigns = Array.isArray(e?.employee_assignments) ? e!.employee_assignments! : [];
  return assigns.some((a) => {
    if (!a || a.is_active === false) return false;
    if (a.store_id == null || a.store_id === "") return false;
    const from = assignmentYmd(a.start_date);
    return from !== null && from > today;
  });
}

/**
 * 配属が無い従業員を抜き出す。
 *
 * 「無い」は**どの店舗にも今日効いている配属が 1 件も無い**こと。
 * ⚠️ 「選んでいる店舗には無いが別の店舗にはある」は数えない。
 * 兼務や異動の途中で暴発するし、その人は別の店では正しく動いているため。
 *
 * @param employees 従業員一覧（`employee_assignments` を eager load した応答）
 * @param today ローカルの YYYY-MM-DD。🔴 **必須**（既定値を置くと未テストの経路ができる。下の 🔴 参照）
 */
export function employeesWithoutAssignment(
  employees: EmployeeLike[] | null | undefined,
  today: string,
): EmployeeLike[] {
  if (!Array.isArray(employees)) return [];
  return employees.filter((e) => {
    if (!needsAssignment(e)) return false;
    const assigns = Array.isArray(e.employee_assignments) ? e.employee_assignments : [];
    if (assigns.some((a) => isAssignmentEffective(a, today))) return false;
    // 開始日が先の配属は「設定済み」として数えない（上の `hasFutureAssignment` の 🔴）
    if (hasFutureAssignment(e, today)) return false;
    return true;
  });
}

/**
 * アカウントが紐づいているか（＝招待を受諾済みか）。
 *
 * 🔴 これが false の人は**配属できない**。配属モーダルは `user_id === 0` で保存を止めるが、
 * `errors.user_id` を描画している箇所が無いので、画面には「入力内容に誤りがあります」しか出ない
 * （全項目が正しく埋まったまま赤くならない）。だから「配属しろ」と案内してはいけない。
 */
export function hasAccount(e: EmployeeLike | null | undefined): boolean {
  if (!e) return false;
  const v = e.user_id;
  // ⚠️ `Number("") === 0` なので空文字はこの下でも false になるが、
  //    意図（「無い」は無い）を残すために明示する。
  if (v == null || v === "") return false;
  return Number(v) > 0;
}

/**
 * 招待がどこまで進んでいるか。
 *
 * 🔴 **`user_id` が無い人を一括りにしてはいけない**。
 * 「まだ招待を送っていない」と「送ったが未受諾」で**やる人が違う**ので、
 * 前者に「受諾してもらってください」と出すと、店側は永久に本人を待つことになる
 * （新規の店の既定状態は**未招待**。2026-10-18 のレビューで発覚）。
 *
 * ⚠️ サーバは**期限切れを見ていない**（`accepted_at` の有無だけ）ので、
 * `'invited'` には期限切れも入る。文面で「期限切れなら再送」を添える。
 */
export type InvitationState = 'accepted' | 'invited' | 'none';

export function invitationState(e: EmployeeLike | null | undefined): InvitationState {
  if (hasAccount(e)) return 'accepted';
  // 受諾済みの判定は `user_id` を正とする（配属の可否を決めるのは `user_id`）。
  const s = e?.latest_invitation_status;
  // ⚠️ `'accepted'` なのに `user_id` が無い行は本来できない（受諾時に埋まる）。
  //    もし出たら「招待を送る」ではなく「状態を確かめる」が正しいので、こちら側に寄せる。
  if (s === 'invited' || s === 'accepted') return 'invited';
  return 'none';
}

/**
 * 配属が無い人を、**やることが違う 2 つ**に分ける。
 *
 * - `notInvited`: **まだ招待を送っていない** → **店側が招待を送る**。配属はできない
 * - `invitePending`: 招待済みで未受諾 → **本人に受諾してもらう**（期限切れなら再送）。配属はできない
 * - `pending`: 上の 2 つの合計（`user_id` が無い人）
 * - `assignable`: 受諾済みで配属が無い → **配属する**
 *
 * 🔴 分けないと「配属しろ」と言いながら配属できない人を指すことになる
 * （導入直後は未受諾が普通なので、この機能が最も効くべき局面で導線が完結しない）。
 */
export function unassignedBreakdown(
  employees: EmployeeLike[] | null | undefined,
  today: string,
): {
  all: EmployeeLike[];
  pending: EmployeeLike[];
  notInvited: EmployeeLike[];
  invitePending: EmployeeLike[];
  assignable: EmployeeLike[];
} {
  const all = employeesWithoutAssignment(employees, today);
  const pending = all.filter((e) => !hasAccount(e));
  return {
    all,
    pending,
    notInvited: pending.filter((e) => invitationState(e) === 'none'),
    invitePending: pending.filter((e) => invitationState(e) === 'invited'),
    assignable: all.filter((e) => hasAccount(e)),
  };
}

/**
 * 一覧を取り切れていないか（件数が過少な可能性があるか）。
 *
 * 🔴 `GET /api/employees` の `per_page` の既定は **15**。引数なしで呼ぶと 16 人以上の会社で
 * 配属が無い人を数え落とし、しかも見えている人を配属すると**警告が消えて「全部終わった」ように見える**。
 * 呼び側は `per_page` を明示するが、それでも取り切れない場合に印を出す。
 * ⚠️ コンテキストに直接書かない（テストが当たらない。`CLAUDE.md` §5）。
 */
export function isEmployeeListTruncated(
  total: unknown,
  returnedCount: number,
): boolean {
  const n = Number(total);
  // ⚠️ `NaN > n` と `0 > n` はどちらも false なので、`undefined` / 文字列 / `null` は
  //    このガードが無くても false になる。**効いているのは `Infinity` の場合だけ**
  //    （`Infinity > 15` は true なので、ガードが無いと「取りこぼし」と誤判定する）。
  if (!Number.isFinite(n)) return false;
  return n > returnedCount;
}

/** 警告に出す件数（`employeesWithoutAssignment` の長さ）。 */
export function countEmployeesWithoutAssignment(
  employees: EmployeeLike[] | null | undefined,
  today: string,
): number {
  return employeesWithoutAssignment(employees, today).length;
}
